import { LAYOUT_ALGORITHM } from "Common/dictionaries/layoutAlgorithm";
import get from "lodash/get";
import { ComponentAssignmentStrategy, EdgeRouter, EdgeRouterEdgeRoutingStyle, EdgeRouterScope, LayoutExecutor, LayoutOrientation, OrganicEdgeRouter, PartialLayout, PartialLayoutData, PartialLayoutEdgeRoutingStrategy, PartialLayoutOrientation, SubgraphLayoutData, SubgraphPlacement, TimeSpan } from "yfiles";
import layoutAlgorithmMap from "./layoutAlgorithms/layoutAlgorithmMap";
let timer = null;
export default (graphComponentRef, layoutAlgorithmRef, affectedNodes, affectedEdges, setInProgress, completed, rejected, animateViewport, duration = "0.4s") => {
    var _a;
    if (!((_a = graphComponentRef.current) === null || _a === void 0 ? void 0 : _a.graph) || !graphComponentRef.current.div.isConnected)
        return;
    const tooManyNodes = Boolean(affectedNodes && affectedNodes.size > 30);
    const tooManyEdges = Boolean(affectedEdges && affectedEdges.size > 40);
    setInProgress && setInProgress(true);
    const layoutAlgorithm = (layoutAlgorithmRef.current && layoutAlgorithmMap[layoutAlgorithmRef.current]
        ? layoutAlgorithmMap[layoutAlgorithmRef.current]
        : layoutAlgorithmMap[LAYOUT_ALGORITHM.HIERARCHIC])();
    const coreLayout = layoutAlgorithm.createConfiguredLayout
        ? layoutAlgorithm.createConfiguredLayout(graphComponentRef.current)
        : null;
    if (!coreLayout) {
        rejected && rejected();
        setInProgress && setInProgress(false);
        return;
    }
    const layoutData = tooManyNodes
        ? new SubgraphLayoutData({
            subgraphNodes: affectedNodes ? Array.from(affectedNodes) : undefined,
            subgraphEdges: affectedEdges ? Array.from(affectedEdges) : undefined
        })
        : new PartialLayoutData({
            affectedNodes: affectedNodes ? Array.from(affectedNodes) : undefined,
            affectedEdges: affectedEdges ? Array.from(affectedEdges) : undefined,
            directedEdges: affectedEdges ? Array.from(affectedEdges) : undefined
        });
    let edgeRouter = null;
    const isOrthogonalEdges = layoutAlgorithmRef.current === LAYOUT_ALGORITHM.ORTHOGONAL ||
        layoutAlgorithmRef.current === LAYOUT_ALGORITHM.HIERARCHIC ||
        layoutAlgorithmRef.current === LAYOUT_ALGORITHM.TREE;
    if (layoutAlgorithmRef.current === LAYOUT_ALGORITHM.ORGANIC) {
        edgeRouter = new OrganicEdgeRouter(coreLayout);
        edgeRouter.keepExistingBends = false;
        edgeRouter.edgeNodeOverlapAllowed = false;
        edgeRouter.routeAllEdges = false;
        edgeRouter.minimumDistance = 10.0;
    }
    else {
        edgeRouter = new EdgeRouter(coreLayout);
        edgeRouter.scope = EdgeRouterScope.ROUTE_EDGES_AT_AFFECTED_NODES;
        edgeRouter.considerNodeLabels = false;
        edgeRouter.considerEdgeLabels = false;
        edgeRouter.ignoreInnerNodeLabels = true;
        edgeRouter.polylineRouting = isOrthogonalEdges;
        edgeRouter.rerouting = isOrthogonalEdges;
        edgeRouter.minimumNodeToEdgeDistance = 0;
        edgeRouter.affectedNodesDpKey = PartialLayout.AFFECTED_NODES_DP_KEY;
        edgeRouter.affectedEdgesDpKey = PartialLayout.DIRECTED_EDGES_DP_KEY;
        edgeRouter.defaultEdgeLayoutDescriptor.routingStyle = isOrthogonalEdges
            ? EdgeRouterEdgeRoutingStyle.ORTHOGONAL
            : EdgeRouterEdgeRoutingStyle.OCTILINEAR;
        edgeRouter.defaultEdgeLayoutDescriptor.minimumEdgeToEdgeDistance = 5.0;
        if (!isOrthogonalEdges) {
            edgeRouter.defaultEdgeLayoutDescriptor.preferredOctilinearSegmentLength = 1;
            edgeRouter.defaultEdgeLayoutDescriptor.maximumOctilinearSegmentRatio = 0.5;
        }
        edgeRouter.defaultEdgeLayoutDescriptor.createControlPoints = false;
        edgeRouter.defaultEdgeLayoutDescriptor.minimumFirstSegmentLength = 10.0;
        edgeRouter.defaultEdgeLayoutDescriptor.minimumLastSegmentLength = 10.0;
    }
    const maximumDurationSec = Math.min(Math.max(10, Math.floor(((affectedNodes === null || affectedNodes === void 0 ? void 0 : affectedNodes.size) || 0) / 10) + 1), 60);
    const partialLayout = new PartialLayout({
        coreLayout,
        componentAssignmentStrategy: ComponentAssignmentStrategy.CONNECTED,
        subgraphPlacement: SubgraphPlacement.BARYCENTER,
        edgeRouter,
        edgeRoutingStrategy: isOrthogonalEdges
            ? PartialLayoutEdgeRoutingStrategy.ORTHOGONAL
            : layoutAlgorithmRef.current === LAYOUT_ALGORITHM.ORGANIC
                ? PartialLayoutEdgeRoutingStrategy.ORGANIC
                : layoutAlgorithmRef.current === LAYOUT_ALGORITHM.CIRCULAR
                    ? PartialLayoutEdgeRoutingStrategy.OCTILINEAR
                    : PartialLayoutEdgeRoutingStrategy.AUTOMATIC,
        layoutOrientation: get(coreLayout, "layoutOrientation") === LayoutOrientation.TOP_TO_BOTTOM
            ? PartialLayoutOrientation.TOP_TO_BOTTOM
            : PartialLayoutOrientation.AUTO_DETECT,
        minimumNodeDistance: get(coreLayout, "minimumNodeDistance", 30.0),
        allowMirroring: true,
        considerNodeAlignment: true,
        componentCompaction: true,
        optimizeOrientation: !tooManyEdges,
        allowMovingFixedElements: false,
        routeInterEdgesImmediately: false,
        resizeFixedGroups: false,
        maximumDuration: maximumDurationSec * 1000
    });
    const layoutExecutor = new LayoutExecutor({
        graphComponent: graphComponentRef.current,
        layout: partialLayout,
        duration,
        layoutData,
        animateViewport: Boolean(animateViewport),
        easedAnimation: true,
        updateContentRect: true,
        allowUserInteraction: false,
        sequentialExecution: true,
        automaticEdgeGrouping: false,
        configureTableLayout: false,
        portAdjustmentPolicy: "lengthen",
        considerViewportLimiter: false,
        hideEdgesAtEdges: true,
        fixPorts: false
    });
    layoutExecutor.abortHandler.cancelDuration = TimeSpan.from(`${maximumDurationSec}s`);
    const callback = () => {
        try {
            if (!graphComponentRef.current || !graphComponentRef.current.div.isConnected) {
                rejected && rejected();
            }
            layoutExecutor
                .start()
                .then(() => {
                completed && completed();
                setInProgress && setInProgress(false);
            })
                .catch(ex => {
                console.warn(ex);
                rejected && rejected();
                setInProgress && setInProgress(false);
            });
        }
        catch (ex) {
            console.warn(ex);
            rejected && rejected();
            setInProgress && setInProgress(false);
        }
    };
    if (affectedNodes && affectedNodes.size > 0) {
        timer && clearTimeout(timer);
        timer = setTimeout(callback, 0);
    }
    else {
        callback();
    }
};
